/**
 * **The service picker** — C25, ADR-0054 §5, T-4.135 (waves.md Wave 27 I-6).
 *
 * Five sections hold service slugs — `services`, `contact`'s
 * `form_service_slugs`, `product_spotlight`, `lead_form` and `pricing` — and
 * until C25 a firm chose them by typing addresses into a box, one a line. This
 * draws the firm's own services instead: by category, in the firm's order, each
 * a tick box, held to the field's own maximum (`pricing` four,
 * `product_spotlight` twelve, the rest thirty-six — served by the vocabulary,
 * never retyped here).
 *
 * The field still holds `service_slugs`, a list of slugs, and the service
 * still resolves them at render (ADR-0022 §5). What the picker adds is three
 * things the box could not say:
 *
 * - **"All services" is a choice** — `services`, `contact` and `lead_form`. It
 *   is not offered on `pricing` or `product_spotlight`, which need at least one
 *   (`required` in the schema), so *empty* is a mistake there and not a
 *   meaning. **The choice is stored** (C27, ADR-0058, UAT report #8): ADR-0054
 *   §5 stored *All services* as an empty list, so *Choose services* with
 *   nothing ticked was the same empty list and drew every service — the
 *   opposite of what the firm chose. The picker now writes `service_pick`
 *   (`contact`: `form_service_pick`) beside the slugs, `all` or `chosen`, on
 *   every change; `chosen` with none ticked draws none, and the hint under the
 *   picker says so. A section saved before C27 has no pick, which reads as
 *   before: empty is all.
 * - **A slug that no longer resolves** — a service deleted, paused, taken off
 *   the website or renamed out of its address — is shown as a chip the firm can
 *   remove, rather than silently kept or silently dropped. It still counts
 *   against the maximum, because the service counts it.
 * - **The order is the firm's.** A choice is stored in the catalogue's order
 *   (category, then service), so the section draws its services in the order
 *   the picker lists them. A slug no longer in the catalogue keeps its place at
 *   the end until it is removed.
 *
 * **What it lists is what the website can draw**: the services `PageEditor`
 * read as active and *shown on your website*, handed down by
 * `ServiceCatalogProvider` — the `IconSetProvider` pattern. A user who cannot
 * read the catalogue (`service:can_view`) gets no provider, and `BlockForm`
 * keeps the box for them.
 */

import { Badge, Button, Checkbox, RadioGroup } from '@fiveox/ui'
import { X } from 'lucide-react'
import { createContext, useContext, useId, useState, type ReactNode } from 'react'

import type { PickerCategory, PickerService, ServiceCatalog } from '../../../lib/api/services'
import { NO_SERVICES_CHOSEN } from '../../../../site/blocks/visibility'

/** How a section's services were chosen (ADR-0058 §1). */
export type ServicePick = 'all' | 'chosen'

/** A stored pick, or `null` for anything else — a section saved before C27. */
export function asServicePick(value: unknown): ServicePick | null {
  return value === 'all' || value === 'chosen' ? value : null
}

/* ── the catalogue, handed down ────────────────────────────────────────── */

const ServiceCatalogContext = createContext<ServiceCatalog | null>(null)

/** Hands the firm's catalogue to every slug field under it (`BlockForm`). `null`
 *  — not readable, not read yet, or the read failed — keeps the box. */
export function ServiceCatalogProvider({ value, children }: { value: ServiceCatalog | null; children: ReactNode }) {
  return <ServiceCatalogContext.Provider value={value}>{children}</ServiceCatalogContext.Provider>
}

/** I-6: the catalogue the picker draws from, or `null` for the box. */
export function useServiceCatalog(): ServiceCatalog | null {
  return useContext(ServiceCatalogContext)
}

/* ── the rules, pure, so they are tested without a screen ─────────────── */

export interface PickerGroup {
  /** `null` for services whose category the catalogue did not name. */
  category: PickerCategory | null
  services: PickerService[]
}

/** The catalogue by category, in the firm's category order; each category's
 *  services in the order the catalogue gave them. Empty categories are left out. */
export function pickerGroups(catalog: ServiceCatalog): PickerGroup[] {
  const groups: PickerGroup[] = catalog.categories.map((category) => ({
    category,
    services: catalog.services.filter((s) => s.category === category.slug),
  }))
  const known = new Set(catalog.categories.map((c) => c.slug))
  const loose = catalog.services.filter((s) => !s.category || !known.has(s.category))
  if (loose.length > 0) groups.push({ category: null, services: loose })
  return groups.filter((g) => g.services.length > 0)
}

/** The slugs in `value` that the catalogue does not list, in their order. */
export function unresolvedSlugs(value: readonly string[], catalog: ServiceCatalog): string[] {
  const listed = new Set(catalog.services.map((s) => s.slug))
  return value.filter((slug) => !listed.has(slug))
}

/**
 * `value` with `slug` ticked or unticked: **the catalogue's order**, then the
 * slugs it does not list, where they were. No slug twice.
 */
export function toggleSlug(value: readonly string[], slug: string, on: boolean, catalog: ServiceCatalog): string[] {
  const chosen = new Set(value)
  if (on) chosen.add(slug)
  else chosen.delete(slug)
  const ordered = pickerGroups(catalog)
    .flatMap((g) => g.services)
    .map((s) => s.slug)
    .filter((s) => chosen.has(s))
  const rest = [...new Set(value)].filter((s) => chosen.has(s) && !ordered.includes(s))
  return [...ordered, ...rest]
}

/* ── the control ───────────────────────────────────────────────────────── */

export interface ServicePickerProps {
  value: string[]
  /** The slugs, and — where *All services* is offered — the pick, always both,
   *  so a section never holds a pick that disagrees with its list. */
  onChange: (next: string[], pick?: ServicePick) => void
  /** The stored `service_pick`. Absent: empty means all (ADR-0058 §1). */
  pick?: ServicePick | null
  /** What an empty *chosen* does: a `services` section is then not drawn
   *  (`section`), an enquiry form asks no service (`form`). */
  empties?: 'section' | 'form'
  /** The field's own maximum, from the schema (`max_items`). */
  max: number
  /** True where an empty list means *every service* — `services`, `contact`,
   *  `lead_form`. False where the schema requires at least one. */
  emptyMeansAll: boolean
  label: string
  /** The catalogue; the provider's when not given. */
  catalog?: ServiceCatalog | null
  error?: string | null
}

export function ServicePicker({
  value,
  onChange,
  pick,
  empties = 'section',
  max,
  emptyMeansAll,
  label,
  catalog,
  error,
}: ServicePickerProps) {
  const fromProvider = useServiceCatalog()
  const list = catalog ?? fromProvider
  const hintId = useId()
  // The pick as this screen last set it, for a parent that does not store one
  // (no `service_pick` in its schema). A stored pick always wins.
  const [localPick, setLocalPick] = useState<ServicePick | null>(null)
  if (!list) return null

  const groups = pickerGroups(list)
  const dead = unresolvedSlugs(value, list)
  const chosen = new Set(value)
  const full = value.length >= max
  const mode: ServicePick = pick ?? localPick ?? (value.length === 0 ? 'all' : 'chosen')
  const all = emptyMeansAll && mode === 'all'
  /** Every change, with the pick beside it where there is one to keep. */
  const change = (next: string[], nextPick: ServicePick = 'chosen') => {
    if (!emptyMeansAll) {
      onChange(next)
      return
    }
    setLocalPick(nextPick)
    onChange(next, nextPick)
  }

  const legend = (
    <span className="flex items-baseline justify-between gap-2">
      <span className="text-sm font-medium text-foreground">{label}</span>
      <span className="text-xs text-muted-foreground">{emptyMeansAll ? 'optional' : 'required'}</span>
    </span>
  )

  return (
    <fieldset className="space-y-2.5" aria-describedby={hintId} data-service-picker="">
      <legend className="mb-1.5 w-full">{legend}</legend>

      {emptyMeansAll ? (
        <RadioGroup
          legend={label}
          hideLegend
          value={all ? 'all' : 'some'}
          options={[
            {
              value: 'all',
              label: 'All services',
              description: 'Every service on your website, in your order — and any you add later.',
            },
            { value: 'some', label: 'Choose services', description: `Up to ${max}, from the list below.` },
          ]}
          onChange={(next) => {
            // *All* clears the list, as it always has; *Choose* keeps what is
            // ticked and — the C27 change — says so in storage, so an empty
            // choice is stored as a choice and not as *all*.
            if (next === 'all') change([], 'all')
            else change(value, 'chosen')
          }}
        />
      ) : null}

      {all ? null : (
        <div className="space-y-3 rounded-lg border border-border p-3" data-service-choices="">
          {groups.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No services are on your website yet. Turn on <em>Show on our website</em> for a service in Services to
              choose it here.
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.category?.slug ?? '-'} data-picker-category={group.category?.slug ?? ''}>
                <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {group.category?.name ?? 'Other services'}
                </p>
                <div className="grid gap-1.5 sm:grid-cols-2">
                  {group.services.map((service) => {
                    const on = chosen.has(service.slug)
                    return (
                      <Checkbox
                        key={service.slug}
                        label={service.name}
                        checked={on}
                        disabled={!on && full}
                        data-slug={service.slug}
                        onChange={(e) => change(toggleSlug(value, service.slug, e.target.checked, list))}
                      />
                    )
                  })}
                </div>
              </div>
            ))
          )}

          {dead.length > 0 ? (
            <div className="space-y-1.5" data-unresolved="">
              <p className="text-xs text-muted-foreground">
                Not on your website any more — deleted, paused, hidden or moved. Remove {dead.length === 1 ? 'it' : 'them'}{' '}
                to free the place.
              </p>
              <ul className="flex flex-wrap gap-1.5">
                {dead.map((slug) => (
                  <li key={slug} className="flex items-center gap-0.5">
                    <Badge tone="warning" size="sm">
                      <span className="font-mono">{slug}</span>
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove ${slug}`}
                      onClick={() => change(value.filter((s) => s !== slug))}
                    >
                      <X size={14} />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}

      <p id={hintId} className="text-xs text-muted-foreground" data-picker-hint="">
        {pickerHint({ value, max, emptyMeansAll, all, empties })}
      </p>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </fieldset>
  )
}

/** The line under the picker: how many are chosen of how many it takes, and
 *  what an empty choice does here. */
export function pickerHint({
  value,
  max,
  emptyMeansAll,
  all,
  empties = 'section',
}: {
  value: readonly string[]
  max: number
  emptyMeansAll: boolean
  all: boolean
  empties?: 'section' | 'form'
}): string {
  if (all) return 'Shows every service on your website.'
  if (value.length === 0) {
    // C27, ADR-0058 §3: *Choose services* with none ticked draws none. It
    // used to say every service was shown until one was ticked — which was
    // true, and was the defect (UAT report #8).
    if (!emptyMeansAll) return `Choose at least one, up to ${max}.`
    return empties === 'form'
      ? 'No services chosen — the form won’t ask which service. Tick at least one, or choose All services.'
      : NO_SERVICES_CHOSEN
  }
  const count = `${value.length} of ${max} chosen.`
  return value.length >= max ? `${count} That is the most this section takes.` : count
}
